<template>
  <v-container
    class="mainContainerBackgroundDebtInput mb-16"
    :class="$vuetify.display.xs ? '' : 'mainContainerBorder'"
  >
    <v-row>
      <v-col cols="12" class="xForClose text-right" @click="closeThisPage"
        ><v-icon :icon="mdiClose"
      /></v-col>
    </v-row>
    <v-row>
      <v-col
        cols="12"
        class="pt-0 pb-0 text-center"
        :class="$vuetify.display.xs ? 'enterDetailsOfThisDebtMob' : 'enterDetailsOfThisDebt'"
      >
        {{ $t('L_enter_details_of_this_debt') }}
      </v-col>
      <v-col
        cols="12"
        class="pt-0 pb-0 text-center"
        :class="$vuetify.display.xs ? 'allFieldsAreRequiredMob' : 'allFieldsAreRequired'"
      >
        {{ $t('L_all_fields_are_required') }}
      </v-col>

      <!-- Debt Type -->
      <v-col cols="12" class="pt-0 pb-0">
        <OnexSelect
          :items="debtCategoryForSelect"
          variant="solo"
          single-line
          :placeholder="getLabelDebtCategory"
          v-model="debtData.debtCategory"
          @update:model-value="isValidInterestRate()"
          :label="$t('L_debt_type')"
          :menu-props="{ location: 'bottom center', scrollStrategy: 'close' }"
        />
      </v-col>

      <!-- Debt Name -->
      <v-col cols="12" class="pt-0 pb-0">
        <OnexText
          :label="$t('L_debt_name')"
          required
          :placeholder="$t('L_debt_name')"
          v-model="debtData.debtName"
          @keyup="isValidDebtName()"
          @change="isValidDebtName()"
          :error-messages="showInvalidDebtName ? [$t('L_invalid_debt_name_error')] : []"
        />
      </v-col>

      <!-- Current Balance -->
      <v-col cols="12" class="pt-0 pb-0">
        <OnexText
          style="font-size: 16px !important"
          id="current_balance"
          :class="{ 'error-affix': showInvalidCurrentBalance }"
          :label="$t('L_current_balance')"
          required
          :placeholder="$t('L_current_balance')"
          v-model="balanceDisplay"
          @update:model-value="sanitizeBalanceInput"
          type="text"
          :error-messages="
            showInvalidCurrentBalance
              ? [$t('L_balance_needs_to_be_between_one_and_million_dollars')]
              : []
          "
          pattern="^\d*(\.\d{0,2})?$"
          inputmode="decimal"
          step="0.01"
          prefix="$"
          @keyup="
            validateMonthlyPayment();
            isValidCurrentBalance();
          "
          @change="
            validateMonthlyPayment();
            isValidCurrentBalance();
          "
          @keydown.e.capture.prevent.stop
        />
      </v-col>

      <!-- Monthly Payment -->
      <v-col cols="12" class="pt-0 pb-0">
        <OnexText
          id="monthly_payment"
          :class="{ 'error-affix': minPaymentError || showInvalidMinPayment }"
          :label="getLabelMonthlyPayment"
          required
          :placeholder="$t('L_monthly_payment')"
          v-model="minPaymentDisplay"
          @update:model-value="sanitizeMinPaymentInput"
          :error-messages="[
            ...(minPaymentError
              ? [$t('L_payment_must_be_greater') + ' ' + minPaymentErrorMsg]
              : []),
            ...(showInvalidMinPayment ? [$t('L_payment_cannot_exceed')] : []),
          ]"
          prefix="$"
          type="text"
          inputmode="decimal"
          @keyup="
            validateMonthlyPayment();
            isValidMonthlyIncome();
          "
          @change="
            validateMonthlyPayment();
            isValidMonthlyIncome();
          "
          @keydown.e.capture.prevent.stop
        />
        <div v-if="minPaymentError" class="text-error text-caption ma-0 pa-0 pl-4">
          <span class="detailsText cursor-pointer" @click="detailsMessage">{{
            $t('L_Details')
          }}</span>
        </div>
      </v-col>

      <!-- Interest Rate -->
      <v-col cols="12" class="pt-0 pb-0">
        <OnexText
          id="interest_rate"
          :class="{ 'error-affix': showInvalidInterestRate }"
          :label="$t('L_interest_rate')"
          required
          :placeholder="$t('L_interest_rate')"
          v-model="interestRateDisplay"
          @update:model-value="sanitizeInterestRateInput"
          :error-messages="
            showInvalidInterestRate
              ? [
                  debtData.debtCategory === 1
                    ? $t('L_valid_range_is_zero_to_fifty')
                    : $t('L_valid_range_is_zero_to_hundred'),
                ]
              : []
          "
          type="text"
          inputmode="decimal"
          suffix="%"
          @keyup="
            validateMonthlyPayment();
            isValidInterestRate();
          "
          @change="
            validateMonthlyPayment();
            isValidInterestRate();
          "
          @keydown.e.capture.prevent.stop
        />
      </v-col>
    </v-row>

    <v-row class="mb-8 py-4 text-center" v-if="!newDebt" pt-4>
      <v-col cols="12">
        <OnexButton
          :class="formValid ? 'primary' : 'graybutton'"
          class="btnFormat maxButtonWidth"
          @click="sendDebtSolutionDataToParent"
          :disabled="!formValid"
        >
          {{ $t('L_save_changes') }}
        </OnexButton>
      </v-col>
      <v-col cols="12">
        <OnexButton
          @click="showDebtAlert"
          variant="outlined"
          primary
          class="btnFormat removeDebtBtn maxButtonWidth"
          :disabled="debtData.debtID === undefined"
        >
          {{ $t('L_remove_debt') }}
        </OnexButton>
      </v-col>
    </v-row>
    <v-row class="mb-8 py-4 text-center" v-else>
      <v-col cols="12">
        <OnexButton
          :class="formValid ? 'primary' : 'graybutton'"
          class="btnFormat maxButtonWidth"
          @click="sendDebtSolutionDataToParent"
          :disabled="!formValid"
        >
          {{ $t('L_add_to_summary') }}
        </OnexButton>
      </v-col>
    </v-row>

    <div v-if="showDetailsMessage || showDebtDeleteAlert" class="modal">
      <MessageDialog
        :show-dialog="showDetailsMessage"
        :has-button-two="true"
        type="warning"
        :title="$t('L_payment_amount_too_low')"
        :message="$t('L_payment_amt_verbiage', { amount: minPaymentErrorMsg })"
        :button-one-label="$t('L_got_it')"
        :is-persistent="true"
        @buttonOneAction="closeTheDetailsInfo"
      />
      <MessageDialog
        :show-dialog="showDebtDeleteAlert"
        :has-button-two="true"
        type="warning"
        :title="$t('L_remove_debt_ques')"
        :message="$t('L_once_you_remove_this_debt')"
        :button-two-label="$t('L_cancel')"
        :button-one-label="$t('L_remove_debt')"
        :is-persistent="true"
        @buttonTwoAction="closeTheDebtDeleteAlert"
        @buttonOneAction="closeTheDebtDeleteAlertAndDeleteData"
      />
    </div>
  </v-container>
</template>

<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import MessageDialog from '@/components/MessageDialog.vue';
import { useDebtSolutionsStore } from '../store/debtsolutions.store';
import { mapActions } from 'pinia';
import { mdiClose } from '@mdi/js';
import { OnexButton, OnexSelect, OnexText } from '@primerica/onex-ui-lib';

const formatThousands = (rawValue: string) => {
  const parts = rawValue.split('.');
  const [integerPartRaw, fractionPart = ''] = parts;
  const integerPartWithCommas = (integerPartRaw || '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.length > 1 ? `${integerPartWithCommas}.${fractionPart}` : integerPartWithCommas;
};

const syncFormattedInputDisplay = (vm: {
  debtData: {
    balance?: string | number | null;
    minPayment?: string | number | null;
    interestRate?: string | number | null;
  };
  balanceDisplay: string;
  minPaymentDisplay: string;
  interestRateDisplay: string;
}) => {
  const balanceRaw = vm.debtData.balance != null ? String(vm.debtData.balance) : '';
  const minPaymentRaw = vm.debtData.minPayment != null ? String(vm.debtData.minPayment) : '';
  const interestRateRaw = vm.debtData.interestRate != null ? String(vm.debtData.interestRate) : '';

  vm.balanceDisplay = balanceRaw === '' ? '' : formatThousands(balanceRaw);
  vm.minPaymentDisplay = minPaymentRaw === '' ? '' : formatThousands(minPaymentRaw);
  vm.interestRateDisplay = interestRateRaw === '' ? '' : formatThousands(interestRateRaw);
};

const componentOptions: ComponentOptions = {
  name: 'DebtInput',
  props: {
    tempDebtData: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },
  },
  components: {
    MessageDialog,
    OnexButton,
    OnexSelect,
    OnexText,
  },
  data: () => ({
    newDebt: true,
    minPaymentError: false,
    minPaymentErrorMsg: 0,
    showDetailsMessage: false,
    showDebtDeleteAlert: false,
    isDisabledSendDebtSolutionRemoval: false,
    isDisabledCloseTheDebt: false,
    debtData: {},
    showInvalidDebtName: false,
    showInvalidCurrentBalance: false,
    showInvalidMinPayment: false,
    showInvalidInterestRate: false,
    balanceDisplay: '',
    minPaymentDisplay: '',
    interestRateDisplay: '',
    mdiClose,
  }),
  mounted() {
    if (this.tempDebtData != null) {
      this.debtData = JSON.parse(JSON.stringify(this.tempDebtData));
      this.debtData.interestRate = (this.debtData.interestRate * 100).toFixed(3);
      this.newDebt = false;
    }
    syncFormattedInputDisplay(this);
  },
  beforeDestroy() {
    if (this.tempDebtData != null) {
      this.debtData = null;
    }
  },
  watch: {
    tempDebtData(val) {
      this.debtData = val;
      syncFormattedInputDisplay(this);
    },
  },
  methods: {
    ...mapActions(useDebtSolutionsStore, {
      updateDebtItem: 'updateDebtItem',
    }),
    validateMonthlyPayment() {
      this.minPaymentError = false;
      if (
        this.debtData.balance !== undefined &&
        this.debtData.minPayment !== undefined &&
        this.debtData.interestRate !== undefined
      ) {
        // Adding an extra penny because for cases where the third number after decimal is less than 5 it would not be rounded up
        const minAllowed = (this.debtData.balance * this.debtData.interestRate) / 1200 + 2.01;
        const minPayment = Number.parseFloat(this.debtData.minPayment) || 0;
        if (minPayment < minAllowed) {
          this.minPaymentErrorMsg = minAllowed;
          this.minPaymentError = true;
        } else {
          this.minPaymentError = false;
        }
      } else {
        this.minPaymentError = false;
      }
    },
    detailsMessage() {
      this.showDetailsMessage = true;
    },
    /*
      getRandomInt(min, max) {
      const tmin = Math.ceil(min);
      const tmax = Math.floor(max);
      // eslint-disable-next-line no-mixed-operators
      return Math.floor(Math.random() * (tmax - tmin + 1)) + tmin;
    },
    */
    isValidDebtName() {
      if (this.debtData.debtName == null || this.debtData.debtName === '') {
        this.showInvalidDebtName = true;
      } else {
        this.debtData.debtName = this.debtData.debtName.replace(/[^A-Za-z0-9 ]/g, '');
        this.debtData.debtName = this.debtData.debtName.substring(0, 50);
        this.showInvalidDebtName = false;
      }
    },
    sanitizeBalanceInput(value: string | number | null | undefined) {
      const cleaned = String(value ?? '').replace(/[^0-9.]/g, '');
      const [integerPartRaw, ...fractionParts] = cleaned.split('.');
      const integerPart = integerPartRaw.replace(/^0+(?=\d)/, '') || '0';

      if (fractionParts.length === 0) {
        this.debtData.balance = integerPart;
        this.balanceDisplay = formatThousands(integerPart);
        return;
      }

      const fractionPart = fractionParts.join('').slice(0, 2);
      this.debtData.balance = `${integerPart}.${fractionPart}`;
      this.balanceDisplay = formatThousands(this.debtData.balance);
    },
    sanitizeMinPaymentInput(value: string | number | null | undefined) {
      // This allows numbers and decimal points to be entered. It removes any other characters that are not numbers or decimal points. This is to avoid cases where the user enters 123abc.45 which would be invalid.
      const cleaned = String(value ?? '').replace(/[^0-9.]/g, '');
      // This splits the cleaned string into an array where the first element is the integer part and the rest are the fraction parts. The spread operator is used to capture all fraction parts in case there are multiple decimal points entered by the user.
      const [integerPartRaw, ...fractionParts] = cleaned.split('.');
      // This removes the leading zeros from the integer part of the number. If the integer part is empty after removing leading zeroes it will set it to 0. This is to avoid cases where the user enters 000000.00 which would be invalid.
      // Default will be 0 if no integer entered. Or if decimal and numbers entered there will be 0.2343 etc
      const integerPart = integerPartRaw.replace(/^0+(?=\d)/, '') || '0';
      // If there are no fraction parts, it sets the minPayment to just the integer part. This is to avoid cases where the user enters 123. which would be invalid.
      if (fractionParts.length === 0) {
        this.debtData.minPayment = integerPart;
        this.minPaymentDisplay = formatThousands(integerPart);
        return;
      }
      // We just want two digits after decimal point. So we join all the fraction parts and slice it to 2 digits. This is to avoid cases where the user enters 123.456 which would be invalid.
      const fractionPart = fractionParts.join('').slice(0, 2);
      this.debtData.minPayment = `${integerPart}.${fractionPart}`;
      this.minPaymentDisplay = formatThousands(this.debtData.minPayment);
    },
    sanitizeInterestRateInput(value: string | number | null | undefined) {
      const cleaned = String(value ?? '').replace(/[^0-9.]/g, '');
      const [integerPartRaw, ...fractionParts] = cleaned.split('.');
      const integerPart = integerPartRaw.replace(/^0+(?=\d)/, '') || '0';

      if (fractionParts.length === 0) {
        this.debtData.interestRate = integerPart;
        this.interestRateDisplay = formatThousands(integerPart);
        return;
      }

      const fractionPart = fractionParts.join('').slice(0, 3);
      this.debtData.interestRate = `${integerPart}.${fractionPart}`;
      this.interestRateDisplay = formatThousands(this.debtData.interestRate);
    },
    isValidCurrentBalance() {
      if (this.debtData.balance == null || this.debtData.balance === '') {
        this.showInvalidCurrentBalance = true;
      } else if (
        !this.debtData.paidOffInd &&
        (this.debtData.balance < 2 || this.debtData.balance > 9999999)
      ) {
        // Max allowed is less than $1 million and more than $1
        this.showInvalidCurrentBalance = true;
      } else if (
        this.debtData.balance === 0 &&
        (this.debtData.paidOffInd !== 1 || this.debtData.paidOffInd !== true)
      ) {
        // balance cannot be 0 unless the paid off indicator is true
        this.showInvalidCurrentBalance = true;
      } else {
        this.showInvalidCurrentBalance = false;
      }
    },
    isValidMonthlyIncome() {
      if (this.debtData.minPayment == null || this.debtData.minPayment === '') {
        this.showInvalidMinPayment = false;
      } else if (this.debtData.minPayment > 999999) {
        this.showInvalidMinPayment = true;
      } else {
        this.showInvalidMinPayment = false;
      }
    },
    isValidInterestRate() {
      if (this.debtData.debtCategory === 1) {
        // Mortgage needs to be between 0% and 50%
        if (this.debtData.interestRate < 0 || this.debtData.interestRate > 50) {
          this.showInvalidInterestRate = true;
        } else {
          this.showInvalidInterestRate = false;
        }
      } else if (this.debtData.interestRate < 0 || this.debtData.interestRate >= 100) {
        // Everything else is between 0% and 99.999%
        this.showInvalidInterestRate = true;
      } else {
        this.showInvalidInterestRate = false;
      }
    },
    setTypeForCategory(category: number) {
      switch (category) {
        case 1:
          this.debtData.mortgageInd = 1;
          this.debtData.debtType = 0;
          break;
        case 2:
        case 5:
          this.debtData.mortgageInd = 0;
          this.debtData.debtType = 1;
          break;
        case 3:
        case 4:
          this.debtData.mortgageInd = 0;
          this.debtData.debtType = 0;
          break;
        default:
          break;
      }
    },
    sendDebtSolutionDataToParent() {
      this.debtData.debtCategory = Number.parseInt(this.debtData.debtCategory, 10) || 0;
      this.debtData.debtName = this.debtData.debtName.substring(0, 50);

      const tempBalance = Number.parseInt(this.debtData.balance, 10) || 0;
      this.debtData.balance = Number.parseInt(tempBalance.toFixed(0), 10) || 0;

      const tempMinPayment = Number.parseFloat(this.debtData.minPayment) || 0;
      this.debtData.minPayment = this.debtData.actualPayment =
        Number.parseFloat(tempMinPayment.toFixed(2)) || 0;

      this.debtData.interestRate = (Number.parseFloat(this.debtData.interestRate) || 0) / 100;
      this.debtData.paidOffInd = this.debtData?.paidOffInd === true ? 1 : 0;

      if (this.debtData.paidOffInd === 1) {
        this.debtData.balance = 0;
      }

      // Currently there is no option on Front end to accelerate debt so we are having to put in here.
      this.debtData.accelerateDebt = 1;

      // Need to set debtType to Revolving or 1 for Credit Card and HELOC
      // For Home Equity Loan, Loan (auto, student, personal) and Mortgage will be Fixed or 0.
      // This is being set only for mortgage
      this.setTypeForCategory(this.debtData.debtCategory);

      // const tTotalDuration = (tCurrentBalance * ((tInterestRate + 100) / 100) / tMonthlyPayment).toFixed(0);

      // this.debtData.totalDuration = tTotalDuration;

      // this.debtData.totalInterestPaid = tTotalDuration * tInterestRate;
      // Will use the one in DebtDetails
      // this.updateDebtItem(this.debtData);

      // This will pass data in this component to parent
      this.$emit('close-debt-input', this.debtData);
    },
    sendDebtSolutionRemovalInstructionToParent() {
      if (this.isDisabledSendDebtSolutionRemoval) {
        return;
      }
      this.isDisabledSendDebtSolutionRemoval = true;
      this.debtData.remove = true;
      this.$emit('close-debt-input', this.debtData);
      setTimeout(() => {
        this.isDisabledSendDebtSolutionRemoval = false;
      }, 500);
    },
    closeThisPage() {
      this.$emit('close-debt-input');
    },
    closeTheDetailsInfo() {
      this.showDetailsMessage = false;
    },
    closeTheDebtDeleteAlert() {
      this.showDebtDeleteAlert = false;
    },
    closeTheDebtDeleteAlertAndDeleteData() {
      if (this.isDisabledCloseTheDebt) {
        return;
      }
      this.isDisabledCloseTheDebt = true;
      this.showDebtDeleteAlert = false;
      this.sendDebtSolutionRemovalInstructionToParent();
      setTimeout(() => {
        this.isDisabledCloseTheDebt = false;
      }, 500);
    },
    showDebtAlert() {
      this.showDebtDeleteAlert = true;
    },
    removeDebtAlert() {
      this.showDebtDeleteAlert = false;
    },
  },
  computed: {
    debtCategoryForSelect() {
      return [
        {
          value: 2,
          title: this.$t('L_credit_card'),
        },
        {
          value: 4,
          title: this.$t('L_home_equity_loan'),
        },
        {
          value: 5,
          title: this.$t('L_home_equity_line_of_credit'),
        },
        {
          value: 3,
          title: this.$t('L_loan_auto_student_personal'),
        },
        {
          value: 1,
          title: this.$t('L_mortgage'),
        },
      ];
    },
    getLabelDebtCategory() {
      return this.$t('L_debt_type');
    },
    getLabelDebtName() {
      return this.$t('L_debt_name');
    },
    getLabelCurrentBalance() {
      return this.$t('L_current_balance');
    },
    getLabelMonthlyPayment() {
      if (this.debtData.debtCategory === 1) {
        return this.$t('L_principal_and_interest');
      }
      return this.$t('L_monthly_payment');
    },
    getLabelInterestRate() {
      return this.$t('L_interest_rate');
    },
    formValid() {
      return !(
        this.debtData.debtCategory == null ||
        this.debtData.debtCategory === '' ||
        this.debtData.debtName == null ||
        this.debtData.debtName === '' ||
        this.debtData.balance == null ||
        this.debtData.balance === '' ||
        this.debtData.minPayment == null ||
        this.debtData.minPayment === '' ||
        this.debtData.interestRate == null ||
        this.debtData.interestRate === '' ||
        this.minPaymentError === true ||
        this.showInvalidDebtName === true ||
        this.showInvalidCurrentBalance === true ||
        this.showInvalidMinPayment === true ||
        this.showInvalidInterestRate === true
      );
    },
  },
};

export default defineComponent(componentOptions);
</script>
<style lang="scss" scoped>
.mainContainerBackgroundDebtInput {
  background-color: #ffffff;
  padding-left: 20px;
  padding-right: 20px;
}

.mainContainerBorder {
  border: 1px solid black;
}

.max-btn-width {
  max-width: 260px;
  width: 260px;
}

.graybutton {
  color: #adadad;
  background-color: #eeeeee;
}

.xForClose {
  color: #005de3;
  font-weight: 600;
  text-align: center;
  cursor: pointer;
}

.paidoff {
  font-size: 20px;
  font-weight: 600;
}

.btnFormat {
  width: 260px;
}

.theme--light.v-btn.v-btn--disabled:not(.v-btn--flat):not(.v-btn--text):not(.v-btn-outlined) {
  color: #adadad !important;
  background-color: #eeeeee !important;
}

.removeDebtBtn {
  color: #002c56;
}

.inputLabel {
  color: #666666;
  font-size: 12px;
  font-weight: 600;
}

.errorColor {
  color: red;
}

.defaultColor {
  color: #666666;
}

.detailsText {
  text-decoration: underline;
}

.infoTitle {
  font-size: 24px;
  font-weight: 600;
  color: #333333;
}

.moveXToRight {
  font-size: 16px;
  font-weight: 600;
  position: absolute;
  right: 15px;
}

.alertModal {
  position: fixed;
  height: 500px;
  width: 590px;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  padding-top: env(safe-area-inset-top);
  /* Ensures padding below the status bar/notch */
}

.alertModalMobile {
  position: fixed;
  width: 320px;
  height: 700px;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  padding-top: env(safe-area-inset-top);
  /* Ensures padding below the status bar/notch */
}

.modal {
  position: fixed;
  /* Stay in place */
  z-index: 11;
  /* Sit on top */
  left: 0;
  top: 0;
  width: 100%;
  /* Full width */
  height: 100%;
  /* Full height */
  overflow: auto;
  /* Enable scroll if needed */
  background-color: rgb(0, 0, 0);
  /* Fallback color */
  background-color: rgba(0, 0, 0, 0.4);
  /* Black w/ opacity */
  padding-top: env(safe-area-inset-top);
  /* Ensures padding below the status bar/notch */
}

.cancelText {
  font-size: 16px;
  color: #333333;
  font-weight: 600;
  position: absolute;
  right: 200px;
  bottom: 15px;
}

.v-input {
  padding-bottom: 5px;
}

:deep(.error-affix .v-field__prefix),
:deep(.error-affix .v-field__suffix),
:deep(.error-affix .v-text-field__prefix),
:deep(.error-affix .v-text-field__suffix) {
  color: #d32f2f !important;
}

.enterDetailsOfThisDebt {
  font-size: 24px;
}

.enterDetailsOfThisDebtMob {
  font-size: 20px;
}

.allFieldsAreRequired {
  font-size: 20px;
}

.allFieldsAreRequiredMob {
  font-size: 16px;
}

.maxButtonWidth {
  max-width: 275px;
  width: 275px;
  padding-left: 0px;
  padding-right: 0px;
  margin-left: 0px;
  margin-right: 0px;
}
</style>
